import {
  Button,
  Input,
  Form,
  Modal,
  message,
  List,
  Select,
  Checkbox,
  InputNumber,
  Space,
  Row,
  Col,
  DatePicker,
} from "antd";
import {
  MinusCircleOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useState, useRef, useContext, useEffect } from "react";
import dayjs from "dayjs";
import { formatCurrency, getPercentageAmount } from "../../helpers/currency";
import { NAIRA, PERCENT } from "../../defaults/utilities";
import {
  extractValueFromInputRef,
  numberToWords,
} from "../../helpers/dom";
import { userStore } from "../../store/userStore";
import {
  useCreateVoucher,
  useUpdateVoucher,
} from "../../hooks/voucher/voucherQuery";
import { useDownloadMemo } from "../../hooks/memo/memoQuery";
import RefreshContext from "../../context/refreshContext";
import DataTable from "../common/table";

const { TextArea } = Input;
const { Option } = Select;

function VoucherForm({
  isOpen,
  onClose,
  voucher,
  years,
  memos,
  allTaxes,
  budgetItems,
}) {
  const [amount, setAmount] = useState(0);
  const [taxes, setTaxes] = useState([]);
  const [taxTotal, setTaxTotal] = useState(0);
  const [currentItem, setCurrentItem] = useState({});
  const [items, setItems] = useState([]);
  const [pvType, setPvType] = useState(0);
  const [fYear, setFYear] = useState(0);
  const [memo, setMemo] = useState("");
  const [checked, setChecked] = useState(false);
  const [inflowsAndOutflows, setInflowsAndOutflows] = useState([]);
  const [currentInflowOutflow, setCurrentInflowOutflow] = useState({});
  const [form] = Form.useForm();

  const { flag, setFlag } = useContext(RefreshContext);
  const currentUser = userStore((state) => state.user);

  const createVoucher = useCreateVoucher();
  const updateVoucher = useUpdateVoucher();
  const downloadMemo = useDownloadMemo();

  const nameRef = useRef(null);
  const descriptionRef = useRef(null);

  // Initialize form when voucher changes
  useEffect(() => {
    if (voucher && isOpen) {
      // Set form fields
      form.setFieldsValue({
        name: voucher.name || '',
        year: voucher.financialYear?.id || voucher.financialYear || undefined,
        description: voucher.description || '',
        memo: voucher.memo?.id || voucher.memo || undefined,
      });

      // Set state values
      setFYear(voucher.financialYear?.id || voucher.financialYear || 0);
      setMemo(voucher.memo?.id || voucher.memo || '');
      setChecked(voucher.declaration || false);

      // Set items from particulars
      if (voucher.particulars && voucher.particulars.length > 0) {
        const formattedItems = voucher.particulars.map(p => ({
          particular: JSON.stringify({
            id: p.particular?.id || p.particular,
            value: p.particular?.title || p.particular?.value || ''
          }),
          amount: p.amount || 0
        }));
        setItems(formattedItems);
        
        // Calculate total amount
        const totalAmount = formattedItems.reduce((sum, item) => sum + item.amount, 0);
        setAmount(totalAmount);
      }

      // Set taxes
      if (voucher.taxes && voucher.taxes.length > 0) {
        const taxIds = voucher.taxes.map(t => t.id);
        setTaxes(taxIds);
        
        // Calculate tax total if amount is set
        if (amount > 0) {
          let total = 0;
          voucher.taxes.forEach(tax => {
            total += getPercentageAmount(amount, tax.charge);
          });
          setTaxTotal(total);
        }
      }

      // Set inflows and outflows
      if (voucher.inflowsAndOutflows && voucher.inflowsAndOutflows.length > 0) {
        // Format the inflows/outflows data for the form
        const formattedInflowsOutflows = voucher.inflowsAndOutflows.map(item => ({
          amount: item.amount,
          date: item.date,
          sourceAccountId: item.sourceAccount?.id || item.sourceAccountId,
          destinationAccountId: item.destinationAccount?.id || item.destinationAccountId
        }));
        setInflowsAndOutflows(formattedInflowsOutflows);
      }
    } else if (!isOpen) {
      // Reset form when modal closes
      form.resetFields();
      setAmount(0);
      setTaxes([]);
      setTaxTotal(0);
      setItems([]);
      setFYear(0);
      setMemo('');
      setChecked(false);
      setInflowsAndOutflows([]);
      setCurrentItem({});
      setCurrentInflowOutflow({});
    }
  }, [voucher, isOpen, form]);

  // Recalculate tax total when amount or taxes change
  useEffect(() => {
    if (taxes.length > 0 && amount > 0 && allTaxes.length > 0) {
      let total = 0;
      taxes.forEach(taxId => {
        const tax = allTaxes.find(t => t.id === taxId);
        if (tax) {
          total += getPercentageAmount(amount, tax.charge);
        }
      });
      setTaxTotal(total);
    }
  }, [amount, taxes, allTaxes]);

  const budegtHeadColumns = [
    {
      title: "Item",
      key: "value",
      dataIndex: "particular",
      render: (item) => JSON.parse(item).value,
    },
    {
      title: "Amount",
      key: "amount",
      dataIndex: "amount",
      render: (amount) => `${NAIRA} ${formatCurrency(amount)}`,
    },
    {
      title: "Action",
      key: "action",
      render: (_, __, idx) => (
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            onClick={() => removeItem(idx)}
            type="primary"
            style={{ backgroundColor: "lightred" }}
          >
            Remove Item
          </Button>
        </div>
      ),
    },
  ];

  const inflowOutflowColumns = [
    {
      title: "Amount",
      key: "amount",
      dataIndex: "amount",
      render: (amount) => `${NAIRA} ${formatCurrency(amount)}`,
    },
    {
      title: "Date",
      key: "date",
      dataIndex: "date",
      render: (date) => {
        // Format the date for display
        if (date) {
          return dayjs(date).format('YYYY-MM-DD');
        }
        return 'N/A';
      },
    },
    {
      title: "Source Account",
      key: "sourceAccount",
      dataIndex: "sourceAccountId",
      render: (sourceAccountId, record) => {
        // First check if the record has sourceAccount object
        if (record.sourceAccount?.title) {
          return record.sourceAccount.title;
        }
        // Otherwise find in budgetItems
        const account = budgetItems.find(item => item.id === parseInt(sourceAccountId));
        return account?.title || 'N/A';
      },
    },
    {
      title: "Destination Account",
      key: "destinationAccount",
      dataIndex: "destinationAccountId",
      render: (destinationAccountId, record) => {
        // First check if the record has destinationAccount object
        if (record.destinationAccount?.title) {
          return record.destinationAccount.title;
        }
        // Otherwise find in budgetItems
        const account = budgetItems.find(item => item.id === parseInt(destinationAccountId));
        return account?.title || 'N/A';
      },
    },
    {
      title: "Action",
      key: "action",
      render: (_, __, idx) => (
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            onClick={() => removeInflowOutflow(idx)}
            type="primary"
            style={{ backgroundColor: "lightred" }}
          >
            Remove
          </Button>
        </div>
      ),
    },
  ];

  function addItem(callback1, callback2) {
    if (Object.keys(currentItem).length < 1) {
      message.error("Cannot add invalid budget item");
      return;
    }
    setItems((prev) => [...prev, currentItem]);
    setCurrentItem({});
    callback1();
    callback2();
    currentItem &&
      currentItem.amount &&
      setAmount(
        (prevState) =>
          items
            .map((e) => e.amount)
            .reduce(
              (previousValue, currentValue) => previousValue + currentValue,
              0,
            ) + currentItem.amount,
      );
  }

  function removeItem(id) {
    const item = items.find((_, itemIndex) => itemIndex === id);
    setAmount((prevTotal) => (prevTotal -= +item.amount));
    setItems((prevItems) => prevItems.filter((_, idx) => idx !== id));
  }

  function addInflowOutflow(callback1, callback2) {
    if (Object.keys(currentInflowOutflow).length < 3) {
      message.error("Please fill all inflow/outflow fields");
      return;
    }
    setInflowsAndOutflows((prev) => [...prev, currentInflowOutflow]);
    setCurrentInflowOutflow({});
    callback1();
    callback2();
  }

  function removeInflowOutflow(id) {
    setInflowsAndOutflows((prevItems) => prevItems.filter((_, idx) => idx !== id));
  }

  const handleSubmit = async () => {
    const payload = {
      financialYear: fYear,
      memo,
      declaration: checked,
      taxes,
      particulars: items.map((i) => ({
        ...i,
        particular: JSON.parse(i.particular).id,
      })),
      name: extractValueFromInputRef(nameRef),
      description: descriptionRef.current.resizableTextArea.textArea.value,
      amountInWords: numberToWords(amount),
      inflowsAndOutflows: inflowsAndOutflows.map((item) => ({
        amount: parseFloat(item.amount),
        date: item.date,
        sourceAccountId: parseInt(item.sourceAccountId),
        destinationAccountId: parseInt(item.destinationAccountId),
      })),
    };
    if (!voucher) {
      const { message: response } = await createVoucher(payload);
      message.success(response ?? "Voucher created successfully");
    } else {
      const { message: response } = await updateVoucher(voucher.id, payload);
      message.success(response ?? "Voucher updated successfully");
    }
    setFlag(!flag);
    onClose();
  };

  const handleDownloadDocument = async () => {
    const resp = await downloadMemo(memo);
  };

  const closeModal = () => {
    form.resetFields();
    setAmount(0);
    setTaxes([]);
    setTaxTotal(0);
    setItems([]);
    setFYear(0);
    setMemo('');
    setChecked(false);
    setInflowsAndOutflows([]);
    setCurrentItem({});
    setCurrentInflowOutflow({});
    onClose();
  };

  return (
    <Modal
      centered
      title={voucher ? "Update Voucher" : "Create Voucher"}
      footer={null}
      onCancel={closeModal}
      width={"70vw"}
      open={isOpen}
    >
      <div
        style={{
          height: "70vh",
          overflowY: "scroll",
          width: "100%",
          paddingRight: "2em",
          paddingLeft: "2em",
          marginTop: "3em",
        }}
      >
        <Form form={form}>
          <Form.Item name="name" style={{marginTop: ".4em"}}>
            <Input placeholder="Enter Voucher name" ref={nameRef} />
          </Form.Item>

          <Form.Item name="year">
              <Select
                placeholder="Select Financial Year"
                onChange={(e) => setFYear(e)}
                style={{ }}
              >
                {years.map((y, idx) => (
                  <Option key={idx} value={y.id}>
                    {y.title}
                  </Option>
                ))}
              </Select>
            </Form.Item>

          <Form.Item name="description">
            <TextArea
              ref={descriptionRef}
              placeholder="Enter Voucher Description"
              autoSize={{
                minRows: 2,
                maxRows: 6,
              }}
            />
          </Form.Item>

          <Row
            gutter={2}
            style={{
              marginBottom: "0em",
              marginTop: "0px",
              paddingTop: "0px",
              paddingBottom: "0em",
              maxHeight: "8em",
            }}
            align={"bottom"}
            justify={"space-between"}
          >
            <Col
              span={16}
              style={{
                marginBottom: "0em",
                marginTop: "0em",
                paddingBottom: 0,
              }}
            >
              <h4>Memo/Claim:</h4>
              <Form.Item name="memo">
                <Select
                  showSearch
                  placeholder="please select the memo/claim associated with this document"
                  onChange={(e) => setMemo(e)}
                >
                  {memos?.map((m, idx) => (
                    <Option
                      key={idx}
                      value={m.id}
                    >{`${m.title} (${m.name} by ${m.owner})`}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col
              span={8}
              style={{
                marginBottom: "0em",
                marginTop: "0em",
                paddingBottom: 0,
              }}
            >
              <Form.Item style={{}}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    marginTop: "auto",
                  }}
                >
                  <Button
                    type="primary"
                    disabled={!memo}
                    onClick={handleDownloadDocument}
                  >
                    Download Memo
                  </Button>
                </div>
              </Form.Item>
            </Col>
          </Row>

          {items.length > 0 && (
            <div style={{ marginBottom: "2em" }}>
              <DataTable
                cols={budegtHeadColumns}
                data={items}
                pagination={false}
                rowKey={(item) => item.budgetHead}
                style={{ marginBottom: "10px" }}
              />
            </div>
          )}

          <Form.List
            name="items"
            style={{ width: "100%", marginTop: "20px" }}
          >
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Space
                    key={key}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: 8,
                      width: "100%",
                    }}
                    align="baseline"
                  >
                    <Form.Item
                      {...restField}
                      name={[name, "Head"]}
                      rules={[
                        {
                          required: true,
                          message: "Missing particular",
                        },
                      ]}
                    >
                      <Select
                        showSearch
                        placeholder="Particular"
                        style={{ width: "20em" }}
                        onChange={(e) =>
                          setCurrentItem((prevItem) => ({
                            ...prevItem,
                            particular: e,
                          }))
                        }
                      >
                        {budgetItems.length > 0 &&
                          budgetItems.map((item, idx) => (
                            <Option
                              value={JSON.stringify({
                                id: item.id,
                                value: item.title,
                              })}
                              key={idx}
                            >
                              {item.title}
                            </Option>
                          ))}
                      </Select>
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, "Amount"]}
                      rules={[
                        {
                          required: true,
                          message: "Missing particular Amount",
                        },
                      ]}
                    >
                      <InputNumber
                        style={{ width: "100%" }}
                        prefix={NAIRA}
                        placeholder="Particular Amount"
                        onChange={(e) => {
                          setCurrentItem((prevItem) => ({
                            ...prevItem,
                            amount: e,
                          }));
                        }}
                      />
                    </Form.Item>
                    <Form.Item>
                      <Button
                        type="primary"
                        style={{ backgroundColor: "lime" }}
                        onClick={() => addItem(() => remove(name), add)}
                      >
                        Add particular
                      </Button>
                    </Form.Item>
                    <MinusCircleOutlined onClick={() => remove(name)} />
                  </Space>
                ))}
                <div style={{ justifyContent: "flex-end", display: "flex" }}>
                  <span style={{ fontWeight: "bold" }}>
                    Total : {NAIRA} {amount}
                  </span>
                </div>
                <Form.Item>
                  <Button
                    type="primary"
                    onClick={() => add()}
                    icon={<PlusOutlined />}
                  >
                    {items.length < 1
                      ? "Add Particular Head"
                      : "Add Another particular"}
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
          <Form.Item>
            <Input readOnly value={`${numberToWords(amount)} Naira Only`} />
          </Form.Item>

          {/* Inflows and Outflows Section */}
          <h4>Inflows and Outflows:</h4>
          {inflowsAndOutflows.length > 0 && (
            <div style={{ marginBottom: "2em" }}>
              <DataTable
                cols={inflowOutflowColumns}
                data={inflowsAndOutflows}
                pagination={false}
                rowKey={(item, idx) => `inflow-${idx}`}
                style={{ marginBottom: "10px" }}
              />
            </div>
          )}

          <Form.List
            name="inflowsAndOutflows"
            style={{ width: "100%", marginTop: "10px" }}
          >
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <div 
                    key={key} 
                    style={{ 
                      marginBottom: "15px", 
                      border: "1px solid #d9d9d9", 
                      padding: "16px", 
                      borderRadius: "6px",
                      backgroundColor: "#fafafa"
                    }}
                  >
                    <Row gutter={[16, 16]}>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          {...restField}
                          name={[name, "amount"]}
                          label="Amount"
                          rules={[{ required: true, message: "Amount required" }]}
                        >
                          <InputNumber
                            style={{ width: "100%" }}
                            prefix={NAIRA}
                            placeholder="Enter amount"
                            min={0}
                            onChange={(e) => {
                              setCurrentInflowOutflow((prev) => ({
                                ...prev,
                                amount: e,
                              }));
                            }}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          {...restField}
                          name={[name, "date"]}
                          label="Date"
                          rules={[{ required: true, message: "Date required" }]}
                        >
                          <DatePicker
                            style={{ width: "100%" }}
                            placeholder="Select date"
                            format="YYYY-MM-DD"
                            onChange={(date, dateString) => {
                              setCurrentInflowOutflow((prev) => ({
                                ...prev,
                                date: dateString,
                              }));
                            }}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={[16, 16]}>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          {...restField}
                          name={[name, "sourceAccountId"]}
                          label="Source Account"
                          rules={[{ required: true, message: "Source account required" }]}
                        >
                          <Select
                            showSearch
                            placeholder="Select source account"
                            style={{ width: "100%" }}
                            optionFilterProp="children"
                            onChange={(e) =>
                              setCurrentInflowOutflow((prev) => ({
                                ...prev,
                                sourceAccountId: e,
                              }))
                            }
                          >
                            {budgetItems.length > 0 &&
                              budgetItems.map((item, idx) => (
                                <Option key={idx} value={item.id}>
                                  {item.title}
                                </Option>
                              ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          {...restField}
                          name={[name, "destinationAccountId"]}
                          label="Destination Account"
                          rules={[{ required: true, message: "Destination account required" }]}
                        >
                          <Select
                            showSearch
                            placeholder="Select destination account"
                            style={{ width: "100%" }}
                            optionFilterProp="children"
                            onChange={(e) =>
                              setCurrentInflowOutflow((prev) => ({
                                ...prev,
                                destinationAccountId: e,
                              }))
                            }
                          >
                            {budgetItems.length > 0 &&
                              budgetItems.map((item, idx) => (
                                <Option key={idx} value={item.id}>
                                  {item.title}
                                </Option>
                              ))}
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>
                    <div style={{ 
                      display: "flex", 
                      justifyContent: "space-between", 
                      alignItems: "center",
                      marginTop: "8px",
                      paddingTop: "8px",
                      borderTop: "1px solid #e8e8e8"
                    }}>
                      <Button
                        type="primary"
                        size="small"
                        onClick={() => addInflowOutflow(() => remove(name), add)}
                        style={{ 
                          backgroundColor: "#52c41a",
                          borderColor: "#52c41a"
                        }}
                      >
                        Add Entry
                      </Button>
                      <Button
                        type="text"
                        size="small"
                        danger
                        icon={<MinusCircleOutlined />}
                        onClick={() => remove(name)}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                ))}
                <Form.Item style={{ marginBottom: "16px" }}>
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    icon={<PlusOutlined />}
                    style={{ 
                      width: "100%",
                      height: "40px"
                    }}
                  >
                    {inflowsAndOutflows.length < 1
                      ? "Add Inflow/Outflow Entry"
                      : "Add Another Entry"}
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
          <h4>Taxes:</h4>
          <Form.Item name="taxes">
            <List
              bordered
              style={{ marginBottom: "0.2em" }}
              itemLayout="vertical"
              dataSource={allTaxes}
              renderItem={(tax, idx) => (
                <>
                  <List.Item
                    key={idx}
                    style={{ display: "flex", height: "3em" }}
                  >
                    <Form.Item>
                      <Checkbox
                        defaultChecked={
                          voucher?.taxes?.filter((t) => t.id === tax.id)
                            .length ?? false
                        }
                        value={tax.id}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setTaxes((t) => [...t, e.target.value]);
                            setTaxTotal(
                              (total) =>
                                total +
                                getPercentageAmount(amount, tax.charge),
                            );
                            return;
                          }
                          setTaxes((t) =>
                            t.filter((tid) => tid !== e.target.value),
                          );
                          setTaxTotal(
                            (total) =>
                              total - getPercentageAmount(amount, tax.charge),
                          );
                        }}
                      >
                        {tax.title} {`(${tax.charge}%)`}
                      </Checkbox>
                    </Form.Item>
                    <div>
                      {NAIRA} {getPercentageAmount(amount, tax.charge)}
                    </div>
                  </List.Item>
                </>
              )}
            />
          </Form.Item>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              backgroundColor: "rgba(0, 0, 0, 0.1)",
              marginBottom: "1em",
              padding: "8px",
              borderRadius: "4px",
            }}
          >
            <h4>Tax Total:</h4>
            <h4>
              {NAIRA} {formatCurrency(taxTotal ?? 0)}
            </h4>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              backgroundColor: "rgba(0, 0, 0, 0.1)",
              marginBottom: "1em",
              padding: "8px",
              borderRadius: "4px",
            }}
          >
            <h4>Total:</h4>
            <h4>
              {NAIRA} {formatCurrency(+(amount - taxTotal))}
            </h4>
          </div>
          {!voucher && (
            <Form.Item>
              <Checkbox
                checked={checked}
                onChange={() => setChecked((c) => !c)}
              >
                {" "}
                I here by declare that I,{" "}
                <span style={{ fontWeight: "bold" }}>
                  {currentUser?.name}
                </span>{" "}
                created this payment voucher, on{" "}
                {new Date(Date.now()).toDateString()}
              </Checkbox>
            </Form.Item>
          )}
          <Form.Item
            wrapperCol={{
              span: 8,
              offset: 21,
            }}
            style={{ marginTop: "1em" }}
          >
            {voucher ? (
              <Button
                type="primary"
                disabled={!checked}
                onClick={handleSubmit}
              >
                Update
              </Button>
            ) : (
              <Button
                type="primary"
                disabled={!checked}
                onClick={handleSubmit}
              >
                Create
              </Button>
            )}
          </Form.Item>
        </Form>
      </div>
    </Modal>
  );
}

export default VoucherForm;